import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Ring, CountUp, Bar, Chip, Skeleton, difficulty } from "@/v2/components/ui";
import { levelInfo } from "@/v2/lib/gamification";
import { Flame, Star, CheckCircle2, Trophy, Rocket, Clock, Timer, Zap, TrendingUp } from "lucide-react";

const useCountdown = () => {
  const [t, setT] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      const s = Math.max(0, Math.floor((end - now) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, "0");
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
      const sec = String(s % 60).padStart(2, "0");
      setT(`${h}:${m}:${sec}`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);
  return t;
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const countdown = useCountdown();

  const { data: challenges = [], isLoading } = useQuery({ queryKey: ["challenges"], queryFn: async () => (await api.get("/challenges")).data });
  const { data: board = [] } = useQuery({ queryKey: ["leaderboard"], queryFn: async () => (await api.get("/leaderboard")).data });

  useEffect(() => {
    if (checked) return;
    (async () => {
      try { const { data } = await api.post("/checkin"); if (data.awarded) toast.success(data.message); await refresh(); } catch {}
      setChecked(true);
    })();
  }, [checked, refresh]);

  const total = challenges.length || 20;
  const completed = user?.completed_challenge_ids?.length || 0;
  const progress = Math.round((completed / total) * 100);
  const rank = board.find((b) => b.is_current)?.rank || "-";
  const lvl = levelInfo(user?.points || 0);
  const current = challenges.find((c) => c.status === "in_progress") || challenges.find((c) => c.status === "available");
  const today = new Date().toLocaleDateString("ar-SA-u-nu-latn", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6">
      {/* HERO */}
      <GlassCard gold className="relative overflow-hidden v2-grid v2-pattern p-6 sm:p-9">
        <div className="relative z-10 flex flex-col lg:flex-row gap-8 items-center lg:items-stretch">
          <div className="flex-1 text-center lg:text-start">
            <div className="text-sm text-[#bcc8cf] mb-2">{today}</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">مرحبًا، <span className="v2-gold-text">{user?.name || ""}</span></h1>
            <p className="text-[#bcc8cf] mb-5 max-w-lg">أنت في المستوى <span className="v2-emerald-text font-bold">{lvl.current.name}</span> — واصل رحلتك نحو التميّز.</p>
            <div className="max-w-md">
              <div className="flex justify-between text-xs text-[#bcc8cf] mb-1.5">
                <span>الخبرة (XP) للمستوى التالي</span>
                <span>{lvl.next ? `${lvl.toNext} نقطة للوصول إلى ${lvl.next.name}` : "أعلى مستوى"}</span>
              </div>
              <Bar value={lvl.progress} />
            </div>
            <button onClick={() => current ? navigate(`/journey/${current.id}`) : navigate("/journey")} data-testid="v2-hero-cta" className="v2-btn mt-6">
              <Rocket size={20} /> {current?.status === "in_progress" ? "أكمل تحدي اليوم" : "ابدأ تحدي اليوم"}
            </button>
          </div>
          <div className="flex items-center justify-center">
            <Ring value={lvl.progress} size={168} stroke={14} id="herolvl">
              <img src={user?.avatar} alt={user?.name} className="w-24 h-24 rounded-full object-cover border-2 border-[#bb9661]/50" />
            </Ring>
          </div>
        </div>
      </GlassCard>

      {/* STATS - varied */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard hover className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><Flame size={24} /></div>
          <div><div className="text-2xl font-extrabold"><CountUp value={user?.streak || 0} /></div><div className="text-xs text-[#bcc8cf]">يوم متتالٍ</div></div>
        </GlassCard>
        <GlassCard hover className="p-5">
          <div className="flex items-center justify-between"><Star size={22} className="text-[#e3c799]" /><TrendingUp size={16} className="text-[#91cab8]" /></div>
          <div className="text-2xl font-extrabold mt-2"><CountUp value={user?.points || 0} /></div>
          <div className="text-xs text-[#bcc8cf]">مجموع النقاط</div>
        </GlassCard>
        <GlassCard hover className="p-5 flex items-center gap-4">
          <Ring value={progress} size={58} stroke={7} id="ministat"><span className="text-xs font-bold">{progress}%</span></Ring>
          <div><div className="text-2xl font-extrabold"><CountUp value={completed} /></div><div className="text-xs text-[#bcc8cf]">تحدٍ مكتمل</div></div>
        </GlassCard>
        <GlassCard hover className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center"><Trophy size={24} /></div>
          <div><div className="text-2xl font-extrabold">#<CountUp value={typeof rank === "number" ? rank : 0} /></div><div className="text-xs text-[#bcc8cf]">ترتيبك</div></div>
        </GlassCard>
      </div>

      {/* DAILY CHALLENGE big card */}
      <div>
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Zap size={18} className="text-[#e3c799]" /> تحدي اليوم</h2>
        {isLoading ? <Skeleton className="h-56 w-full" /> : current ? (
          <GlassCard gold hover className="relative overflow-hidden grid md:grid-cols-[280px_1fr]">
            <div className="relative h-44 md:h-full min-h-[180px]">
              <img src={current.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-[#25343f] via-[#25343f]/40 to-transparent" />
              <div className="absolute top-4 right-4 flex gap-2">
                <Chip tone="gold">اليوم {current.day}</Chip>
                <Chip tone={difficulty(current.points).tone}>{difficulty(current.points).label}</Chip>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-extrabold mb-2">{current.title}</h3>
              <p className="text-[#bcc8cf] mb-4 line-clamp-2">{current.description}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#bcc8cf] mb-5">
                <span className="flex items-center gap-1.5"><Clock size={16} /> {current.video_duration}</span>
                <span className="flex items-center gap-1.5"><Star size={16} className="text-[#e3c799]" /> {current.points} نقطة</span>
                <span className="flex items-center gap-1.5 text-[#e3c799]"><Timer size={16} /> متبقٍ اليوم {countdown}</span>
              </div>
              <button onClick={() => navigate(`/journey/${current.id}`)} data-testid="v2-daily-start" className="v2-btn">
                <Rocket size={18} /> ابدأ الآن
              </button>
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="p-10 text-center text-[#bcc8cf]">🎉 أكملت جميع التحديات المتاحة! تابع لوحة المتصدرين.</GlassCard>
        )}
      </div>
    </div>
  );
}
